import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, Download } from "lucide-react";
import { publicApi, mediaUrl } from "../lib/api";
import GuideDownloadModal from "./GuideDownloadModal";
import PromoVideo from "./PromoVideo";
import { consentChoice } from "./CookieConsent";

const KEY = "cass_promo_seen";

// Chosen in Admin → Homepage Promo Popup. "md" is the original size and stays the
// default, so an existing promo is never resized by accident. Mobile ignores this
// — every size is full width inside the overlay's padding on a phone.
export const SIZES = {
  md: "max-w-md",
  lg: "max-w-xl",
  xl: "max-w-3xl",
  full: "max-w-5xl",
};

// Bigger popups need proportionally more breathing room, or a 1,024px card looks
// like a small one that has been stretched.
const SCALE = {
  md: { pad: "p-8 pt-6", heading: "text-2xl", band: "h-24" },
  lg: { pad: "p-8 pt-6 md:p-9 md:pt-7", heading: "text-2xl md:text-3xl", band: "h-24 md:h-28" },
  xl: { pad: "p-8 pt-6 md:p-10 md:pt-8", heading: "text-2xl md:text-3xl", band: "h-24 md:h-32" },
  full: { pad: "p-8 pt-6 md:p-12 md:pt-9", heading: "text-2xl md:text-4xl", band: "h-24 md:h-36" },
};

// A phone shows every size at the same width, so it always gets the Standard
// treatment — a 4xl heading in a 358px-wide card wraps to five lines.
const MOBILE_SCALE = SCALE.md;

/**
 * The popup card itself. Exported so the admin preview renders the real thing
 * rather than a lookalike that can drift out of step with it.
 */
// Pixel widths behind the size classes, so a video card can be narrowed to the
// clip's own shape without exceeding the size that was chosen.
const SIZE_PX = { md: 448, lg: 576, xl: 768, full: 1024 };

export function PromoCard({ promo, onClose = () => {}, onCta = () => {}, preview = false, mobile = false }) {
  const isGuide = promo.kind === "guide";
  const isImageOnly = promo.kind === "image";
  // A video replaces the header image on any promo, and is the whole point of
  // the "video" kind.
  const hasVideo = !!promo.video;
  // The card follows the video's shape: a square or portrait clip narrows the
  // popup instead of being cropped or sitting in black bars.
  const [videoRatio, setVideoRatio] = useState(0);
  const link = promo.link || "/";
  const isExternal = /^https?:\/\//i.test(link);
  const width = SIZES[promo.size] || SIZES.md;
  const scale = mobile ? MOBILE_SCALE : (SCALE[promo.size] || SCALE.md);
  const imgWidth = promo.size === "md" ? 800 : 1600;

  const closeBtn = (cls) => (
    <button
      data-testid={preview ? "promo-preview-close" : "home-promo-close"}
      onClick={preview ? undefined : onClose}
      aria-label="Close"
      tabIndex={preview ? -1 : 0}
      className={cls}
    >
      <X size={18} />
    </button>
  );

  // The media area carries the same action as the button: a visitor who clicks
  // the picture or the clip gets where they were going.
  const clickable = (inner, testid) => {
    if (preview) return <div className="block">{inner}</div>;
    if (isGuide) return <button type="button" onClick={onCta} data-testid={testid} className="block w-full text-left">{inner}</button>;
    if (isExternal) return <a href={link} target="_blank" rel="noopener noreferrer" data-testid={testid} onClick={onCta} className="block">{inner}</a>;
    return <Link to={link} data-testid={testid} onClick={onCta} className="block">{inner}</Link>;
  };

  if (isImageOnly) {
    const inner = promo.image ? (
      <img loading="lazy" decoding="async" src={mediaUrl(promo.image, imgWidth)} alt={promo.heading || "Promotion"} className="w-full h-auto block" />
    ) : (
      <div className="numbers-pattern aspect-[4/3] flex items-center justify-center text-white/70 text-sm font-semibold">Upload an image to see it here</div>
    );
    return (
      <div
        className={`relative w-full ${width} max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200`}
        onClick={(e) => e.stopPropagation()}
        data-testid={preview ? "promo-preview-card" : "home-promo-card"}
        data-size={promo.size || "md"}
      >
        {closeBtn("absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-navy transition-colors z-10 shadow")}
        {preview ? (
          <div className="block">{inner}</div>
        ) : isExternal ? (
          <a href={link} target="_blank" rel="noopener noreferrer" data-testid="home-promo-cta" onClick={onCta} className="block">{inner}</a>
        ) : (
          <Link to={link} data-testid="home-promo-cta" onClick={onCta} className="block">{inner}</Link>
        )}
      </div>
    );
  }

  const ctaCls = "w-full inline-flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5";
  const ctaLabel = promo.button_text || (isGuide ? "Free Download" : "Find out more");

  const fitWidth = hasVideo && videoRatio && !mobile
    ? { maxWidth: `min(${SIZE_PX[promo.size] || SIZE_PX.md}px, calc(50vh * ${videoRatio.toFixed(3)}))` }
    : undefined;

  return (
    <div
      className={`relative w-full ${width} max-h-[90vh] overflow-y-auto bg-white rounded-3xl shadow-2xl animate-in zoom-in-95 duration-200`}
      style={fitWidth}
      data-video-ratio={videoRatio ? videoRatio.toFixed(2) : undefined}
      onClick={(e) => e.stopPropagation()}
      data-testid={preview ? "promo-preview-card" : "home-promo-card"}
      data-size={promo.size || "md"}
    >
      {closeBtn("absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-navy transition-colors z-10 shadow")}

      {hasVideo ? (
        clickable(<PromoVideo promo={promo} preview={preview} onRatio={setVideoRatio} />, "home-promo-video-cta")
      ) : promo.image ? (
        <div className="aspect-[16/9] overflow-hidden bg-navy">
          <img loading="lazy" decoding="async" src={mediaUrl(promo.image, imgWidth)} alt={promo.heading} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`numbers-pattern ${scale.band}`} />
      )}

      <div className={scale.pad}>
        <span className="text-orange font-bold text-xs tracking-widest uppercase">Featured</span>
        <h2 className={`font-display font-extrabold ${scale.heading} text-navy leading-tight mt-1.5`}>
          {promo.heading || (preview ? "Your heading appears here" : "")}
        </h2>
        {promo.body && <p className="text-slate-500 text-sm mt-3 leading-relaxed">{promo.body}</p>}
        {hasVideo && promo.video_transcript && (
          <p className="sr-only" data-testid="home-promo-video-transcript">{promo.video_transcript}</p>
        )}

        <div className="mt-6">
          {preview ? (
            <span className={ctaCls}>{ctaLabel} {isGuide ? <Download size={17} /> : <ArrowRight size={18} />}</span>
          ) : isGuide ? (
            <button data-testid="home-promo-cta" onClick={onCta} className={ctaCls}>
              {ctaLabel} <Download size={17} />
            </button>
          ) : (
            <Link to={link} data-testid="home-promo-cta" onClick={onCta} className={ctaCls}>
              {ctaLabel} <ArrowRight size={18} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HomePromoPopup() {
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(KEY)) return;
    let timer;
    // The cookie banner has to be answered first. This overlay covers the whole
    // screen above it, so opening while the banner is still up swallowed the
    // Accept and Reject clicks — visitors could neither consent nor refuse.
    const show = () => {
      if (sessionStorage.getItem(KEY)) return;
      if (!consentChoice()) {
        timer = setTimeout(show, 1000);
        return;
      }
      sessionStorage.setItem(KEY, "1");
      setOpen(true);
      publicApi.trackPromo("impression");
    };
    publicApi.promo().then((data) => {
      if (!data || !data.enabled) return;
      setPromo(data);
      timer = setTimeout(show, 1800);
    }).catch(() => {});
    return () => clearTimeout(timer);
  }, []);

  const close = () => setOpen(false);

  if (!open || !promo) return null;

  const isGuide = promo.kind === "guide";
  const onCta = () => {
    publicApi.trackPromo("click");
    if (isGuide) setGuideOpen(true);
    close();
  };

  return (
    <>
      <div
        data-testid="home-promo-overlay"
        className="fixed inset-0 z-[120] flex items-center justify-center bg-navy-deep/80 backdrop-blur-sm p-4 animate-in fade-in-0 duration-200"
        onClick={close}
      >
        <PromoCard promo={promo} onClose={close} onCta={onCta} />
      </div>

      {isGuide && promo.guide && (
        <GuideDownloadModal guide={promo.guide} open={guideOpen} onClose={() => setGuideOpen(false)} />
      )}
    </>
  );
}
